/**
 *
 * Main.js
 *
 */

import React, { lazy, Suspense, useEffect, useState } from 'react';
import { compose } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { isLoaded } from 'react-redux-firebase';
import { Helmet } from 'react-helmet-async';
import qs from 'qs';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import _get from 'lodash/get';
import { getLocalData, setLocalData } from 'utils/localDataStore';
import {
  generateKey,
  isBot,
  getSubdomain,
  isEmbedded,
} from 'utils/stringUtils';
import { createStructuredSelector } from 'reselect';
import NavBar from 'components/NavBarMenu';
import Root from 'components/Root';
import Routes from 'components/Routes';
import { useAssessmentReminder } from 'components/Hooks';
import withPageBranding from 'components/PageBranding';
import ErrorBoundary from 'components/ErrorBoundary';
import TopBanner from 'components/TopBanner';
import Hidden from '@material-ui/core/Hidden';
import Ribbon from 'components/Ribbon';
import AICCBackHeader from 'components/AICCBackHeader';
import BackHeader from 'components/BackHeader';
import RegionBanner from 'components/RegionBanner';
import useAICC from 'components/useAICC';
import useAppEmbedMode from 'components/useAppEmbedMode';
import useClinicMode from 'components/useClinicMode';
import useHomepagePro from 'components/useHomepagePro';
import usePageTracking from 'components/UsePageTracking';
import useProfileFilters from 'components/useProfileFilters';
import retry from 'utils/retry';
import { ReactQueryDevtools } from 'react-query-devtools';
import { emitter } from '@marvelapp/react-ab-test';
import { useQuery } from 'react-query';
import { makeSelectAuth, makeSelectProfile } from 'containers/Auth/selectors';
import NPSSurvey from 'components/NPSSurvey';
import LanguageBanner from 'components/LanguageBanner';
import LeavingAppDialog from 'components/LeavingAppDialog';
import InsuranceModal from 'components/InsuranceModal';
import withPageLanguage from 'components/PageLanguage';
import OnboardingQuestionsDialog from 'components/Personalizations/OnboardingQuestionsDialog';
import { LanguageDisclaimer } from 'components/LanguageDisclaimer/LanguageDisclaimer';
import globalSaga from './globalSaga';
import {
  getAudienceTagsRelations,
  getPageIntros,
  getReferrals,
  getSiteCopy,
  getSiteConfig,
  setLanguage,
  showLanguageBanner,
  getLocales,
} from './actions';
import getEmbeddedUrlByShortName from './services/getEmbeddedUrl.gql';
import {
  makeSelectClientDetails,
  makeSelectClientDetailsFetching,
  makeSelectClientDetailsLoaded,
  makeSelectLanguage,
  makeSelectShowHomepagePro,
  makeSelectSiteCopy,
  makeSelectSiteConfig,
  makeSelectLocales,
  makeSelectClinicMode,
} from './selectors';
import { isNpsSurvey } from './utils';

const { Slide, toast } = !isBot() ? require('react-toastify') : {};

const UserConsent = lazy(() => retry(() => import('components/UserConsent')));
const TermsOfUse = lazy(() => retry(() => import('containers/TermsOfUse')));
const PrivacyPolicy = lazy(() =>
  retry(() => import('containers/PrivacyPolicy')),
);
const AuthModal = lazy(() => retry(() => import('components/AuthModal')));
const AuthCallToActionModal = lazy(() =>
  retry(() => import('components/AuthCallToActionModal')),
);
const FooterWrapper = lazy(() =>
  retry(() => import('components/FooterWrapper')),
);
// const Sponsors = lazy(() => retry(() => import('components/SponsorFooter')));
// const Footer = lazy(() => retry(() => import('components/Footer')));
const RegionSelector = lazy(() =>
  retry(() => import('components/RegionSelector')),
);

if (toast) {
  // eslint-disable-next-line global-require
  require('react-toastify/dist/ReactToastify.css');
  toast.configure({
    hideProgressBar: true,
    transition: Slide,
  });
}

emitter.defineVariants(
  'hero-variant',
  ['variantA', 'variantB', 'variantC', 'variantD'],
  [25, 25, 25, 25],
);
emitter.defineVariants(
  'assessment-results-lists',
  ['grouped', 'tabbed'],
  [50, 50],
);
emitter.calculateActiveVariant('assessment-results-lists');

const stateSelector = createStructuredSelector({
  clientDetails: makeSelectClientDetails(),
  clientDetailsFetching: makeSelectClientDetailsFetching(),
  clientDetailsLoaded: makeSelectClientDetailsLoaded(),
  auth: makeSelectAuth(),
  showHomepagePro: makeSelectShowHomepagePro(),
  siteCopy: makeSelectSiteCopy(),
  siteConfig: makeSelectSiteConfig(),
  language: makeSelectLanguage(),
  locales: makeSelectLocales(),
  profile: makeSelectProfile(),
  clinicMode: makeSelectClinicMode(),
});

function Main() {
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [hasMultipleLanguage, setHasMultipleLanguage] = useState(false);
  const brandName = getLocalData('brand') || 'none';
  const [isBranded, setIsBranded] = useState(brandName !== 'none');
  const { pathname, search } = window.location;
  const isHomePage = pathname === '/';
  const {
    clientDetails,
    clientDetailsFetching,
    clientDetailsLoaded,
    auth,
    showHomepagePro,
    siteCopy,
    siteConfig,
    language,
    locales,
    profile,
    clinicMode,
  } = useSelector(stateSelector);

  const bot = isBot();
  const dispatch = useDispatch();
  const clientShortName = _get(clientDetails, 'shortName');
  const isAdmin = pathname.includes('/admin');
  const subdomain = getSubdomain();
  const isEmbed = isEmbedded();
  const isClientAdmin = pathname.includes('/client-admin');
  // fetch embeded url
  const { data: embeddedUrl, isFetching } = useQuery(
    subdomain &&
      !isClientAdmin &&
      subdomain !== 'none' && ['embedded-url', subdomain.toLowerCase()],
    getEmbeddedUrlByShortName,
  );
  const queryParams = qs.parse(search.slice(1));

  useHomepagePro();
  useClinicMode();
  useProfileFilters();

  // init trackers
  usePageTracking();

  useAssessmentReminder();

  // scroll top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (
      !initialLoaded &&
      isLoaded(auth) &&
      !_isEmpty(siteCopy) &&
      !_isEmpty(siteConfig) &&
      !_isNil(clinicMode) &&
      clientDetailsLoaded
    ) {
      const loaderEl = document.getElementById('full-page-loader');
      if (loaderEl) loaderEl.parentNode.removeChild(loaderEl);

      setInitialLoaded(true);
    }
  }, [auth, clinicMode, siteCopy, siteConfig, clientDetailsLoaded]);

  useEffect(() => {
    /** get locales */
    dispatch(getLocales());

    // set temporary user id in cookies if not set yet, this will be used for recombee interaction when user is not logged in
    let userId = getLocalData('userIdTemp');
    if (_isEmpty(userId)) {
      userId = `anonymous-${generateKey()}`;
      setLocalData('userIdTemp', userId, {
        expires: 365,
      });
      setLocalData('isSubscribed', 'false', {
        expires: 365,
      });
    }

    const regex = new RegExp(
      /\/(apps|articles|blogs|books|lists|faqs|organizations|people|podcasts|programs|topics|videos)\/[\S]+/g,
    );
    if (!regex.test(window.location)) {
      window.prerenderReady = true;
    }
  }, []);

  useEffect(() => {
    if (!_isEmpty(locales)) {
      /** Get Home Banner/Hero content */
      dispatch(getSiteCopy());
      dispatch(getSiteConfig());
      dispatch(getPageIntros());
      dispatch(getReferrals());
      /** Get Audience and Tags Relations */
      dispatch(getAudienceTagsRelations());
    }
  }, [dispatch, locales, language]);

  useEffect(() => {
    if (!_isEmpty(clientShortName) && !_isEmpty(clientDetails)) {
      setIsBranded(true);
      setLocalData('brand', clientDetails.shortName);
      const languages = _get(clientDetails, 'languageCollection.items', []);
      const languagesLen = languages.length;
      setHasMultipleLanguage(languagesLen > 1);
      if (languagesLen) {
        dispatch(setLanguage(language || _get(languages, '0.shortcode')));
        dispatch(
          showLanguageBanner(
            languagesLen > 1 &&
              !showHomepagePro &&
              queryParams.showcorp !== '1',
          ),
        );
      } else {
        dispatch(setLanguage(''));
        dispatch(showLanguageBanner(false));
      }
    }

    if (!clientDetails && _isEmpty(clientDetails) && !clientDetailsFetching) {
      setHasMultipleLanguage(false);
      setIsBranded(false);
      setLocalData('brand', 'none');
      dispatch(showLanguageBanner(false));
    }
  }, [clientDetails, clientDetailsFetching]);

  useAICC();
  useAppEmbedMode();

  if (subdomain && isFetching) return null;
  // manually redirect for now, change this once we have ssr
  if (!isEmbed && embeddedUrl && !_isEmpty(embeddedUrl)) {
    const cleanPathname = pathname.replace(/^\/+/g, '');
    const finalRedirectUrl = `${embeddedUrl}${
      cleanPathname ? `?page=${cleanPathname}` : ''
    }`;
    window.location.href = finalRedirectUrl;
  }

  const notACorporateSite = !showHomepagePro && queryParams.showcorp !== '1';
  const showTopBannerAndRibbon =
    notACorporateSite &&
    isHomePage &&
    _isEmpty(clientDetails) &&
    _isEmpty(clientShortName) &&
    !clientDetailsFetching;
  if (!initialLoaded) return null;

  const useAppEmbedStyle =
    isEmbed || _get(clientDetails, 'metadata.useAppEmbedStyle', false);

  const customFontStylesheetsUrls = _get(
    clientDetails,
    'customFontsCollection.items',
    [],
  ).map(item => item.stylesheet.url);
  return (
    <>
      {customFontStylesheetsUrls.length > 0 && (
        <Helmet>
          {customFontStylesheetsUrls.map(url => (
            <link href={url} rel="stylesheet" />
          ))}
        </Helmet>
      )}
      <ErrorBoundary>
        <Suspense fallback={<div />}>
          <UserConsent />
        </Suspense>
        <BackHeader />
        <AICCBackHeader />
        {!useAppEmbedStyle && notACorporateSite && !isClientAdmin && !isAdmin && (
          <>
            {showTopBannerAndRibbon ? (
              <>
                <Ribbon />
                <Hidden xsDown>
                  <TopBanner />
                </Hidden>
              </>
            ) : (
              <Hidden xsDown>
                {hasMultipleLanguage ? (
                  <LanguageBanner hasMultipleLanguage={hasMultipleLanguage} />
                ) : (
                  <RegionBanner />
                )}
              </Hidden>
            )}
          </>
        )}

        {!isClientAdmin && <NavBar />}
        <Root>
          <Routes />
        </Root>
        <Suspense fallback={<div />}>
          <TermsOfUse />
          <PrivacyPolicy />
          <FooterWrapper isBranded={isBranded} />
          <LeavingAppDialog />
          <LanguageDisclaimer clientDetails={clientDetails} />

          {isNpsSurvey(
            showHomepagePro,
            queryParams,
            isClientAdmin,
            profile,
          ) && <NPSSurvey />}
          {!bot && (
            <>
              <OnboardingQuestionsDialog clientShortName={clientShortName} />
              <AuthModal />
              <InsuranceModal />
              <AuthCallToActionModal />
              {isBranded && !_isEmpty(clientShortName) && <RegionSelector />}
            </>
          )}
          {/* <MyLocation /> */}
        </Suspense>
      </ErrorBoundary>
      <ReactQueryDevtools initialIsOpen={false} />
    </>
  );
}

export default compose(
  ...Object.values(globalSaga),
  withPageBranding,
  withPageLanguage,
)(Main);
